import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Navbar } from '../components/Navbar'
import { Footer } from '../components/Footer'
import { Button } from '../components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import {Trophy, Medal, Award, Star, ArrowLeft, Zap, Crown, MapPin, TrendingUp, Users, Sparkles} from 'lucide-react'
import { lumi } from '../lib/lumi'
import { useAuth } from '../hooks/useAuth'
import { REGIONAL_BADGES } from '../data/regionalBadges'

export function RankingNacional() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [topDonors, setTopDonors] = useState<any[]>([])
  const [userProfile, setUserProfile] = useState<any>(null)
  const [userRank, setUserRank] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [totalNationalDonors, setTotalNationalDonors] = useState(0)
  const [totalNationalDonations, setTotalNationalDonations] = useState(0)

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return
      
      try {
        const { list: userProfiles } = await lumi.entities.profiles.list({
          filter: { userId: user.userId }
        })
        if (userProfiles.length > 0) {
          setUserProfile(userProfiles[0])
        }
      } catch (error) {
        console.error('Failed to fetch user profile:', error)
      }
    }
    fetchUserProfile()
  }, [user])

  useEffect(() => {
    const fetchNationalRanking = async () => {
      try {
        setLoading(true)
        
        const { list, total } = await lumi.entities.profiles.list({
          filter: { isDonor: true },
          sort: { points: -1 },
          limit: 50 // Reduzido de 100 para 50 para performance
        })
        
        setTotalNationalDonors(total)
        setTotalNationalDonations(list.reduce((sum, d) => sum + (d.totalDonations || 0), 0))
        setTopDonors(list.slice(0, 50))
        
        if (userProfile) {
          const userPosition = list.findIndex(d => d._id === userProfile._id)
          setUserRank(userPosition !== -1 ? userPosition + 1 : null)
        }
      } catch (error) {
        console.error('Failed to fetch national ranking:', error)
      } finally {
        setLoading(false)
      }
    }
    
    fetchNationalRanking()
  }, [userProfile])

  const getRankIcon = (index: number) => {
    if (index === 0) return <Crown className="h-8 w-8 text-yellow-500 animate-pulse" />
    if (index === 1) return <Medal className="h-7 w-7 text-gray-400" />
    if (index === 2) return <Award className="h-7 w-7 text-orange-600" />
    if (index <= 9) return <Star className="h-6 w-6 text-yellow-500" />
    if (index <= 49) return <Sparkles className="h-5 w-5 text-blue-500" />
    return <Star className="h-5 w-5 text-gray-400" />
  }

  const getRankBg = (index: number) => {
    if (index === 0) return 'bg-gradient-to-r from-yellow-100 via-yellow-50 to-yellow-100 border-yellow-500 border-3 shadow-xl'
    if (index === 1) return 'bg-gradient-to-r from-gray-100 via-gray-50 to-gray-100 border-gray-400 border-2 shadow-lg'
    if (index === 2) return 'bg-gradient-to-r from-orange-100 via-orange-50 to-orange-100 border-orange-400 border-2 shadow-lg'
    if (index <= 9) return 'bg-gradient-to-r from-blue-50 to-blue-100 border-blue-300 shadow-md'
    if (index <= 49) return 'bg-gradient-to-r from-green-50 to-green-100 border-green-300'
    return 'bg-white border-gray-200'
  }

  const getRankBadge = (index: number) => {
    if (index === 0) return (
      <span className="ml-2 px-3 py-1 bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs font-bold rounded-full shadow-md animate-pulse">
        👑 Lenda Nacional
      </span>
    )
    if (index <= 9) return (
      <span className="ml-2 px-2 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold rounded-full shadow-md">
        🏅 Hall da Fama
      </span>
    )
    if (index <= 49) return (
      <span className="ml-2 px-2 py-1 bg-gradient-to-r from-green-500 to-blue-500 text-white text-xs font-bold rounded-full">
        ⭐ Top 50 Brasil
      </span>
    )
    return null
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/ranking')}
          className="mb-4"
          aria-label="Voltar para rankings"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-yellow-500 via-orange-500 to-red-500 flex items-center justify-center shadow-2xl animate-pulse">
              <Crown className="h-12 w-12 text-white" />
            </div>
          </div>
          <h1 className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-600 via-orange-600 to-red-600 mb-2">
            ⭐ Lendas do Brasil
          </h1>
          <p className="text-gray-600 text-lg">
            Top 50 doadores de todo o país
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Os maiores heróis da doação de sangue no Brasil
          </p>
        </div>

        {/* User Position Card */}
        {userProfile && userRank && userRank <= 50 && (
          <Card className="mb-8 border-2 border-yellow-500 bg-gradient-to-r from-yellow-50 via-white to-yellow-50 shadow-2xl">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center text-white font-black text-2xl shadow-lg">
                    {userProfile.displayName?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <div className="flex items-center">
                      <h3 className="font-bold text-2xl text-gray-900">{userProfile.displayName}</h3>
                      {getRankBadge(userRank - 1)}
                    </div>
                    <p className="text-sm text-gray-600">{userProfile.bloodType} • {userProfile.city}/{userProfile.state}</p>
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-600 to-orange-600">
                    #{userRank}
                  </div>
                  <p className="text-xs text-gray-600 font-medium">no Brasil</p>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 border-2 border-yellow-300 shadow-md">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="flex items-center justify-center space-x-1 mb-1">
                      <Zap className="h-5 w-5 text-yellow-500" />
                      <span className="text-2xl font-black text-gray-900">{userProfile.points || 0}</span>
                    </div>
                    <p className="text-xs text-gray-600">pontos</p>
                  </div>
                  <div>
                    <p className="text-2xl font-black text-orange-600">{userProfile.totalDonations || 0}</p>
                    <p className="text-xs text-gray-600">doações</p>
                  </div>
                  <div>
                    <p className="text-2xl font-black text-orange-600">{(userProfile.totalDonations || 0) * 4}</p>
                    <p className="text-xs text-gray-600">vidas salvas</p>
                  </div>
                </div>
              </div>

              {userProfile.badges && userProfile.badges.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-bold text-gray-700 mb-2">Suas Medalhas Nacionais:</p>
                  <div className="flex flex-wrap gap-2">
                    {userProfile.badges
                      .filter((b: string) => REGIONAL_BADGES[b]?.scope === 'national')
                      .map((badge: string) => {
                        const badgeInfo = REGIONAL_BADGES[badge]
                        return badgeInfo ? (
                          <span key={badge} className="px-3 py-1 bg-gradient-to-r from-yellow-100 to-orange-100 rounded-full text-sm font-medium border-2 border-yellow-300 shadow-sm">
                            {badgeInfo.icon} {badgeInfo.name}
                          </span>
                        ) : null
                      })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* National Stats */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-300 shadow-lg">
            <CardContent className="pt-6 text-center">
              <Users className="h-10 w-10 text-blue-600 mx-auto mb-2" />
              <p className="text-4xl font-black text-gray-900">{totalNationalDonors}</p>
              <p className="text-sm text-gray-600 font-medium">doadores no Brasil</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-300 shadow-lg">
            <CardContent className="pt-6 text-center">
              <TrendingUp className="h-10 w-10 text-green-600 mx-auto mb-2" />
              <p className="text-4xl font-black text-gray-900">{totalNationalDonations}</p>
              <p className="text-sm text-gray-600 font-medium">doações totais</p>
            </CardContent>
          </Card>
        </div>

        {/* Top 50 National Ranking */}
        <Card className="shadow-2xl border-2 border-yellow-300">
          <CardHeader className="bg-gradient-to-r from-yellow-50 to-orange-50">
            <CardTitle className="text-3xl font-black flex items-center justify-center">
              <Trophy className="h-8 w-8 text-yellow-500 mr-3" />
              Hall da Fama Nacional
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-24 bg-gray-100 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : topDonors.length > 0 ? (
              <div className="space-y-3">
                {topDonors.map((donor, index) => (
                  <div 
                    key={donor._id} 
                    className={`flex items-center justify-between p-4 rounded-xl border transition-all hover:shadow-lg hover:scale-[1.02] ${getRankBg(index)}`}
                  >
                    <div className="flex items-center space-x-4 flex-1">
                      <div className="flex-shrink-0 w-12 text-center">
                        {getRankIcon(index)}
                      </div>
                      <div className={`${index <= 2 ? 'w-14 h-14' : 'w-12 h-12'} rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center text-white font-bold ${index <= 2 ? 'text-xl' : 'text-lg'} shadow-lg`}>
                        {donor.displayName?.charAt(0) || 'D'}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center flex-wrap">
                          <h4 className={`font-bold text-gray-900 ${index <= 2 ? 'text-lg' : ''}`}>
                            {donor.displayName}
                          </h4>
                          {getRankBadge(index)}
                        </div>
                        <p className="text-sm text-gray-600">
                          {donor.bloodType} • {donor.city}/{donor.state} • {donor.totalDonations || 0} {(donor.totalDonations || 0) === 1 ? 'doação' : 'doações'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center justify-end space-x-1 mb-1">
                        <Zap className="h-5 w-5 text-yellow-500" />
                        <span className={`font-black text-gray-900 ${index <= 2 ? 'text-2xl' : 'text-xl'}`}>
                          {donor.points || 0}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 font-medium">pontos</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Crown className="h-20 w-20 text-gray-300 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Seja uma lenda do Brasil!</h3>
                <p className="text-gray-600 mb-4">Entre para o Hall da Fama Nacional</p>
                <Button onClick={() => navigate('/solicitacoes')} className="mt-2">
                  Ver Pedidos de Doação
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="mt-8 text-center">
          <Button 
            onClick={() => navigate('/solicitacoes')}
            className="w-full max-w-md h-16 text-xl font-bold bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 hover:from-yellow-600 hover:via-orange-600 hover:to-red-600 text-white shadow-xl"
          >
            🩸 Ver Pedidos e Doar Agora
          </Button>
        </div>
      </div>
      <Footer />
    </div>
  )
}
